import React from "react";

import HomeIcon from "@material-ui/icons/Home";
import StarIcon from "@material-ui/icons/Star";
import DevicesIcon from "@material-ui/icons/Devices";
import PremiumSermonIcon from "@material-ui/icons/InsertDriveFileSharp";
import LibraryBooksIcon from "@material-ui/icons/LibraryBooks";
import ArrowDropDown from "@material-ui/icons/ArrowDropDown";
import ArrowRight from "@material-ui/icons/ArrowRight";
import FavoriteIcon from "@material-ui/icons/Whatshot";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import { useOfflineAssets } from "../../offline/useOfflineAssets";
import { LibraryView, useLibrary } from "../LibraryPage";
import { routes } from "../../../routes";
import { LibraryItemType } from "../../../entities/LibraryItemType";
import { useMemo } from "react";
import { TreeBranch } from "./TreeBranch";
import { List, Hidden, ListItem, ListItemIcon, ListItemText, Typography, Collapse, Divider, makeStyles } from "@material-ui/core";
import classNames from "classnames";
import { useHistory } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
	divider: {
		marginBottom: "8px",
	},
	navListScrollable: {
		overflowY: "auto",
		top: 0,
		bottom: 0,
		height: "100%",
		minWidth: "240px",
		maxWidth: "240px",
		overflowX: "hidden",
		whiteSpace: "nowrap",
	},
	listItem: {
		borderRadius: "0px 15px 15px 0px",
		"&:hover": {
			backgroundColor: "#F5F5F5",
		},
		paddingLeft: "2px",
	},
	listItemText: {
		color: "#5f6368",
		fontWeight: 500,
		fontFamily: "Roboto, Helvetica",
		marginLeft: "8px",
	},
	listIconActions: {
		minWidth: 40
	},
	regulartListItem: {
		paddingLeft: "24px",
	},
}));

export interface State {
	folderTreeOpen: boolean;
}

function getRoute(view: LibraryView) {
	switch (view) {
		case LibraryView.Home:
			return routes.app.libraryHomePage;
		case LibraryView.Recent:
			return routes.app.libraryRecentPage;
		case LibraryView.Starred:
			return routes.app.libraryStarredPage;
		case LibraryView.Calendar:
			return routes.app.libraryCalendarPage;
		case LibraryView.OfflineAccess:
			return routes.app.libraryOfflinePage;
		case LibraryView.SermonTemplateList:
			return routes.app.sermonTemplateListPage;
		case LibraryView.PremiumSermonList:
			return routes.app.premiumSermonListPage;
		case LibraryView.PremiumSermonSeriesList:
			return routes.app.premiumSeriesListPage;
		default:
			return routes.app.libraryHomePage;
	}
}

function isReallyAFolder (type: LibraryItemType){
	return type === LibraryItemType.Folder || type === LibraryItemType.PremiumSermon || type === LibraryItemType.PremiumSermonSeries || type === LibraryItemType.SermonSeries;
}

export function SideNavigation() {
	const history = useHistory();
	const context = useLibrary();
	const { libraryItems, activeView, activeFolderId } = context;
	const classes = useStyles();

	const folders = useMemo(() => libraryItems.filter(item => isReallyAFolder(item.type)), [libraryItems]);

	const [folderTreeOpen, setFolderTreeOpen] = React.useState(false);
	const offlineStatus = useOfflineAssets();
	const { onlineMode } = offlineStatus;

	const onNavDoubleClick = (viewType: LibraryView) => {
		history.push(getRoute(viewType));
	};

	const toggleTree = (event: any) => {
		event.preventDefault();
		event.stopPropagation();
		setFolderTreeOpen(!folderTreeOpen);
	};

	const handlePathChange = (path: string) => {
		let folder = folders.find((a) => a.id === path);

		if (folder) {
			history.push(routes.app.resolve.libraryFolder(folder.id));
		}
	};

	return (
		<List dense>
			<Hidden smDown>
				<ListItem
					disabled={!onlineMode}
					button
					selected={activeView === LibraryView.Home && (activeFolderId === "" || !folderTreeOpen)}
					onClick={() => onNavDoubleClick(LibraryView.Home)}
					onDoubleClick={(e) => toggleTree(e)}
					style={{
						backgroundColor: `${
							activeView === LibraryView.Home && (activeFolderId === "" || !folderTreeOpen)
								? "#e8f0fe"
								: ""
						}`,
					}}
					className={classes.listItem}
				>
					<ListItemIcon style={{minWidth: 0}} onClick={(e: any) => toggleTree(e)}>
						{folderTreeOpen ? <ArrowDropDown /> : <ArrowRight />}
					</ListItemIcon>
					<div style={{ display: "contents" }}>
						<ListItemIcon className={classes.listIconActions}>
							<HomeIcon
								style={{
									color: `${
										activeView === LibraryView.Home && (activeFolderId === "" || !folderTreeOpen)
											? "#1967d2"
											: ""
									}`,
								}}
							/>
						</ListItemIcon>
						<ListItemText
							disableTypography
							primary={
								<Typography
									variant="body1"
									className={classes.listItemText}
									style={{
										color: `${
											activeView === LibraryView.Home &&
											(activeFolderId === "" || !folderTreeOpen)
												? "#1967d2"
												: ""
										}`,
										paddingTop: "2px",
									}}
								>
									My Library
								</Typography>
							}
						/>
					</div>
				</ListItem>
				<Collapse in={folderTreeOpen} timeout="auto" unmountOnExit style={{maxHeight: 200, overflowY: "scroll"}}>
					<TreeBranch
						folderLibraryItems={folders}
						parentId={null}
						onSelect={handlePathChange}
						level={1}
						focusedId={activeFolderId ? activeFolderId : ""}
					/>
				</Collapse>
				<Divider className={classes.divider} />
			</Hidden>
			<Hidden mdUp>
				<ListItem
					disabled={!onlineMode}
					button
					selected={activeView === LibraryView.Home}
					onClick={() => onNavDoubleClick(LibraryView.Home)}
					style={{ backgroundColor: `${activeView === LibraryView.Home ? "#e8f0fe" : ""}` }}
					className={classNames(classes.listItem, classes.regulartListItem)}
				>
					<ListItemIcon className={classes.listIconActions}>
						<HomeIcon
							style={{
								color: `${activeView === LibraryView.Home ? "#1967d2" : ""}`,
							}}
						/>
					</ListItemIcon>
					<ListItemText
						disableTypography
						primary={
							<Typography
								variant="body1"
								className={classes.listItemText}
								style={{
									color: `${activeView === LibraryView.Home ? "#1967d2" : ""}`,
								}}
							>
								My Library
							</Typography>
						}
					/>
				</ListItem>
			</Hidden>
			<ListItem
				disabled={!onlineMode}
				button
				selected={activeView === LibraryView.Recent}
				onClick={() => onNavDoubleClick(LibraryView.Recent)}
				style={{ backgroundColor: `${activeView === LibraryView.Recent ? "#e8f0fe" : ""}` }}
				className={classNames(classes.listItem, classes.regulartListItem)}
			>
				<ListItemIcon className={classes.listIconActions}>
					<AccessTimeIcon
						style={{
							color: `${activeView === LibraryView.Recent ? "#1967d2" : ""}`,
						}}
					/>
				</ListItemIcon>
				<ListItemText
					disableTypography
					primary={
						<Typography
							variant="body1"
							className={classes.listItemText}
							style={{
								color: `${activeView === LibraryView.Recent ? "#1967d2" : ""}`,
							}}
						>
							Recent
						</Typography>
					}
				/>
			</ListItem>
			<ListItem
				disabled={!onlineMode}
				button
				selected={activeView === LibraryView.Starred}
				onClick={() => onNavDoubleClick(LibraryView.Starred)}
				style={{
					backgroundColor: `${activeView === LibraryView.Starred ? "#e8f0fe" : ""}`,
					color: `${activeView === LibraryView.Starred ? "#1967d2" : ""}`,
				}}
				className={classNames(classes.listItem, classes.regulartListItem)}
			>
				<ListItemIcon className={classes.listIconActions}>
					<StarIcon
						style={{
							color: `${activeView === LibraryView.Starred ? "#1967d2" : ""}`,
						}}
					/>
				</ListItemIcon>
				<ListItemText
					disableTypography
					primary={
						<Typography
							variant="body1"
							className={classes.listItemText}
							style={{
								color: `${activeView === LibraryView.Starred ? "#1967d2" : ""}`,
							}}
						>
							Starred
						</Typography>
					}
				/>
			</ListItem>
			<ListItem
				disabled={!onlineMode}
				button
				selected={activeView === LibraryView.Calendar}
				onClick={() => onNavDoubleClick(LibraryView.Calendar)}
				style={{ backgroundColor: `${activeView === LibraryView.Calendar ? "#e8f0fe" : ""}` }}
				className={classNames(classes.listItem, classes.regulartListItem)}
			>
				<ListItemIcon className={classes.listIconActions}>
					<CalendarTodayIcon
						style={{
							color: `${activeView === LibraryView.Calendar ? "#1967d2" : ""}`,
						}}
					/>
				</ListItemIcon>
				<ListItemText
					disableTypography
					primary={
						<Typography
							variant="body1"
							className={classes.listItemText}
							style={{
								color: `${activeView === LibraryView.Calendar ? "#1967d2" : ""}`,
							}}
						>
							Calendar
						</Typography>
					}
				/>
			</ListItem>
			<ListItem
				button
				selected={activeView === LibraryView.OfflineAccess}
				onClick={() => onNavDoubleClick(LibraryView.OfflineAccess)}
				style={{ backgroundColor: `${activeView === LibraryView.OfflineAccess ? "#e8f0fe" : ""}` }}
				className={classNames(classes.listItem, classes.regulartListItem)}
			>
				<ListItemIcon className={classes.listIconActions}>
					<DevicesIcon
						style={{
							color: `${activeView === LibraryView.OfflineAccess ? "#1967d2" : ""}`,
						}}
					/>
				</ListItemIcon>
				<ListItemText
					disableTypography
					primary={
						<Typography
							variant="body1"
							className={classes.listItemText}
							style={{
								color: `${activeView === LibraryView.OfflineAccess ? "#1967d2" : ""}`,
							}}
						>
							Offline Access
						</Typography>
					}
				/>
			</ListItem>
			<ListItem
				disabled={!onlineMode}
				button
				selected={activeView === LibraryView.SermonTemplateList}
				onClick={() => onNavDoubleClick(LibraryView.SermonTemplateList)}
				style={{
					backgroundColor: `${activeView === LibraryView.SermonTemplateList ? "#e8f0fe" : ""}`,
					color: `${activeView === LibraryView.SermonTemplateList ? "#1967d2" : ""}`,
				}}
				className={classNames(classes.listItem, classes.regulartListItem)}
			>
				<ListItemIcon className={classes.listIconActions}>
					<FavoriteIcon
						style={{
							color: `${activeView === LibraryView.SermonTemplateList ? "#1967d2" : ""}`,
						}}
					/>
				</ListItemIcon>
				<ListItemText
					disableTypography
					primary={
						<Typography
							variant="body1"
							className={classes.listItemText}
							style={{
								color: `${activeView === LibraryView.SermonTemplateList ? "#1967d2" : ""}`,
							}}
						>
							Sermon Templates
						</Typography>
					}
				/>
			</ListItem>
			<ListItem
				disabled={!onlineMode}
				button
				selected={activeView === LibraryView.PremiumSermonList}
				onClick={() => onNavDoubleClick(LibraryView.PremiumSermonList)}
				style={{
					backgroundColor: `${activeView === LibraryView.PremiumSermonList ? "#e8f0fe" : ""}`,
					color: `${activeView === LibraryView.PremiumSermonList ? "#1967d2" : ""}`,
				}}
				className={classNames(classes.listItem, classes.regulartListItem)}
			>
				<ListItemIcon className={classes.listIconActions}>
					<PremiumSermonIcon
						style={{
							color: `${activeView === LibraryView.PremiumSermonList ? "#1967d2" : ""}`,
						}}
					/>
				</ListItemIcon>
				<ListItemText
					disableTypography
					primary={
						<Typography
							variant="body1"
							className={classes.listItemText}
							style={{
								color: `${activeView === LibraryView.PremiumSermonList ? "#1967d2" : ""}`,
							}}
						>
							Premium Sermons
						</Typography>
					}
				/>
			</ListItem>
			<ListItem
				disabled={!onlineMode}
				button
				selected={activeView === LibraryView.PremiumSermonSeriesList}
				onClick={() => onNavDoubleClick(LibraryView.PremiumSermonSeriesList)}
				style={{
					backgroundColor: `${activeView === LibraryView.PremiumSermonSeriesList ? "#e8f0fe" : ""}`,
					color: `${activeView === LibraryView.PremiumSermonSeriesList ? "#1967d2" : ""}`,
				}}
				className={classNames(classes.listItem, classes.regulartListItem)}
			>
				<ListItemIcon className={classes.listIconActions}>
					<LibraryBooksIcon
						style={{
							color: `${activeView === LibraryView.PremiumSermonSeriesList ? "#1967d2" : ""}`,
						}}
					/>
				</ListItemIcon>
				<ListItemText
					disableTypography
					primary={
						<Typography
							variant="body1"
							className={classes.listItemText}
							style={{
								color: `${activeView === LibraryView.PremiumSermonSeriesList ? "#1967d2" : ""}`,
							}}
						>
							Premium Series
						</Typography>
					}
				/>
			</ListItem>
		</List>
	);
}